import { useEffect, useState } from "react";
import { useSession } from "@/hooks/auth-hooks";
import { signIn, signUp } from "@/lib/auth-client";

function generateRandomUser() {
	const adjectives = [
		"Happy",
		"Lucky",
		"Sunny",
		"Swift",
		"Clever",
		"Brave",
		"Calm",
		"Eager",
	];
	const nouns = [
		"Panda",
		"Tiger",
		"Eagle",
		"Dolphin",
		"Fox",
		"Wolf",
		"Bear",
		"Hawk",
	];
	const randomAdj = adjectives[Math.floor(Math.random() * adjectives.length)];
	const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
	const randomNum = Math.floor(Math.random() * 1000);

	return {
		name: `${randomAdj} ${randomNoun}`,
		email: `${randomAdj.toLowerCase()}.${randomNoun.toLowerCase()}${randomNum}@test.local`,
		password: `Test${randomNum}!pass`,
	};
}

export default function DevTools() {
	const [isOpen, setIsOpen] = useState(false);
	const [status, setStatus] = useState<{
		type: "idle" | "loading" | "success" | "error";
		message?: string;
	}>({ type: "idle" });
	const { refetch } = useSession();

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (
				e.key === "l" &&
				!e.ctrlKey &&
				!e.metaKey &&
				!e.altKey &&
				!(e.target instanceof HTMLInputElement) &&
				!(e.target instanceof HTMLTextAreaElement) &&
				!(e.target as HTMLElement)?.isContentEditable
			) {
				console.info("Language switching removed; app is English-only.");
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, []);

	if (import.meta.env.PROD) return null;

	const handleCreateRandomUser = async () => {
		setStatus({ type: "loading" });
		const randomUser = generateRandomUser();

		try {
			const signUpResult = await signUp.email({
				email: randomUser.email,
				password: randomUser.password,
				name: randomUser.name,
			});

			if (signUpResult.error) {
				setStatus({ type: "error", message: signUpResult.error.message });
				return;
			}

			const verifyResponse = await fetch("/api/dev/create-user", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ email: randomUser.email }),
			});

			const verifyResult = (await verifyResponse.json()) as {
				success: boolean;
				userId?: string;
				error?: string;
			};

			if (!verifyResult.success) {
				setStatus({
					type: "error",
					message: `Created but failed to verify: ${verifyResult.error ?? "Unknown error"}`,
				});
				return;
			}

			const signInResult = await signIn.email({
				email: randomUser.email,
				password: randomUser.password,
			});

			if (signInResult.error) {
				setStatus({ type: "error", message: signInResult.error.message });
				return;
			}

			await refetch();
			setStatus({
				type: "success",
				message: `Created & signed in: ${randomUser.email}`,
			});

			setTimeout(() => {
				setStatus({ type: "idle" });
				window.location.assign("/dashboard");
			}, 1000);
		} catch (err) {
			setStatus({
				type: "error",
				message: err instanceof Error ? err.message : "Failed to create user",
			});
		}
	};

	const handleCreateProUser = async () => {
		setStatus({ type: "loading" });
		const randomUser = generateRandomUser();

		try {
			const signUpResult = await signUp.email({
				email: randomUser.email,
				password: randomUser.password,
				name: randomUser.name,
			});

			if (signUpResult.error) {
				setStatus({ type: "error", message: signUpResult.error.message });
				return;
			}

			const verifyResponse = await fetch("/api/dev/create-user", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ email: randomUser.email }),
			});

			const verifyResult = (await verifyResponse.json()) as {
				success: boolean;
				userId?: string;
				error?: string;
			};

			if (!verifyResult.success || !verifyResult.userId) {
				setStatus({
					type: "error",
					message: `Created but failed to verify: ${verifyResult.error ?? "Unknown error"}`,
				});
				return;
			}

			const signInResult = await signIn.email({
				email: randomUser.email,
				password: randomUser.password,
			});

			if (signInResult.error) {
				setStatus({ type: "error", message: signInResult.error.message });
				return;
			}

			const proResponse = await fetch("/api/dev/set-pro", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ userId: verifyResult.userId }),
			});

			const proResult = (await proResponse.json()) as {
				success: boolean;
				error?: string;
			};

			if (!proResult.success) {
				setStatus({
					type: "error",
					message: `Created but failed to set pro: ${proResult.error ?? "Unknown error"}`,
				});
				return;
			}

			await refetch();
			setStatus({
				type: "success",
				message: `Created Pro user: ${randomUser.email}`,
			});

			setTimeout(() => {
				setStatus({ type: "idle" });
				window.location.assign("/dashboard");
			}, 1000);
		} catch (err) {
			setStatus({
				type: "error",
				message:
					err instanceof Error ? err.message : "Failed to create pro user",
			});
		}
	};

	return (
		<div className="fixed bottom-4 left-4 z-50">
			{isOpen && (
				<div className="absolute bottom-12 left-0 w-64 bg-gray-900 border border-gray-700 rounded-lg shadow-xl p-3 mb-2">
					<div className="flex items-center justify-between mb-3">
						<span className="text-sm font-semibold text-gray-200">
							Dev Tools
						</span>
						<button
							type="button"
							onClick={() => setIsOpen(false)}
							aria-label="Close dev tools"
							className="p-1 hover:bg-gray-800 rounded text-gray-400 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500"
						>
							<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "14px" }}>close</span>
						</button>
					</div>

					<button
						type="button"
						onClick={handleCreateRandomUser}
						disabled={status.type === "loading"}
						aria-busy={status.type === "loading"}
						className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-200 bg-gray-800 hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500"
					>
						<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px" }}>person_add</span>
						<span>Create Random User</span>
					</button>

					<button
						type="button"
						onClick={handleCreateProUser}
						disabled={status.type === "loading"}
						aria-busy={status.type === "loading"}
						className="w-full flex items-center gap-2 px-3 py-2 mt-2 text-sm text-gray-200 bg-gray-800 hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500"
					>
						<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px", color: "#fbbf24", fontVariationSettings: "'FILL' 1" }}>crown</span>
						<span>Create Pro User</span>
					</button>

					{status.type !== "idle" && (
						<div
							className={`mt-2 text-xs px-2 py-1.5 rounded ${
								status.type === "loading"
									? "bg-blue-900/50 text-blue-300"
									: status.type === "success"
										? "bg-green-900/50 text-green-300"
										: "bg-red-900/50 text-red-300"
							}`}
						>
							{status.type === "loading" ? "Processing..." : status.message}
						</div>
					)}
				</div>
			)}

			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				aria-label={isOpen ? "Close developer tools" : "Open developer tools"}
				aria-expanded={isOpen}
				className="p-3 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-full shadow-lg border border-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500"
			>
				<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "20px" }}>build</span>
			</button>
		</div>
	);
}
